// @ts-check

import * as schedulerService from '../../services/Scheduler/schedulerService';
import { gridsNames } from '../../enums/gridsNames';
import * as gridActionsHelper from 'infrastructure/js/components/Grid/Utils/gridActionsHelper';
import DateTimeHelper from 'infrastructure/js/utils/dateTimeHelper';
import { createLabelHelper } from 'infrastructure/js/utils/labelHelper';
import MaterialHelper from '../../utils/materialHelper';
import { enumTypes, getEnumValue, getLookupOptions } from '../../utils/enumHelper';
import PermissionManager from 'infrastructure/js/utils/permissionManager';
import {
  getCompletedQuantityInfo,
  getFormattedSetupTime,
  getFormattedTokenDetails,
  getQuantityInfo
} from '../../components/Common/Helpers/SchedulerHelper';
import Network from 'infrastructure/js/modules/network';
import { actions as schedulerTasksPageActions } from './schedulerTasksPageActions';
import StoreRegistry from 'infrastructure/js/utils/StoreRegistry';



const gridActions = gridActionsHelper.getGridActions(gridsNames.PREDEFINED_LIST_SCHEDULER_OPERATIONS, _fetchDataAction, _convertToRowData);


function _fetchDataAction(query, newAbortController, runSideEffect) {
  return function (dispatch, getState) {
    query.pageSize = PermissionManager.getOrgPreferences().schedulerTasksPageSize || 1000;

    const schedulerTasksGenerated = getState().predefinedList.getIn(['scheduler', 'schedulerTasksGenerated']);
    if (!schedulerTasksGenerated) {
      return schedulerService.generateOperations().then((response) => {
        if (!Network.isResponseValid(response)) {
          console.error('Fetch Scheduler operations Failed');
          return [];
        }

        dispatch(schedulerTasksPageActions.schedulerTasksGenerated());
        return schedulerService.fetchOperations(query);
      });
    }

    return schedulerService.fetchOperations(query);
  };
}


function _convertToRowData(rows) {

  // let t1 = performance.now();

  let labels = createLabelHelper('mat.scheduler.operations.grid.moreInfo.');

  
  let E = {
    OBECT_TYPE__TASK      : getEnumValue(enumTypes.OBJECT_TYPE)('TASK'),
    TASK_STATUS__COMPLETED: getEnumValue(enumTypes.TASK_STATUS)('COMPLETED'),
    
  };
  
  let L = {
    PLANNED_START_TIME : labels.get('plannedStartTime'),
    PLANNED_END_TIME   : labels.get('plannedEndTime'),
    PLANNED_TOOL_ID    : labels.get('plannedToolId'),
    START_TIME         : labels.get('startTime'),
    END_TIME           : labels.get('endTime'),
    TOOL_ID            : labels.get('toolId'),
    PLANNED_DURATION   : labels.get('plannedDuration'),
    PROJECT            : labels.get('project'),
    TOOL_REQUIREMENT   : labels.get('toolRequirement'),
    DURATION           : labels.get('duration'),
    MATERIAL           : labels.get('material'),
    SPLIT_ENABLED      : labels.get('splitEnabled'),
    RECIPE             : labels.get('recipe'),
    KITTYPE_DESCRIPTION: labels.get('kitTypeDescription'),
    TOKENS_DETAILS     : labels.get('tokensDetails'),
    SETUP_TIME         : labels.get('setupTime'),
    COMPLETED_QUANTITY : labels.get('completedQuantity'),
    
  };
  
  let PRIORITY_TYPE__LOOKUP_OPTIONS = getLookupOptions(enumTypes.PRIORITY_TYPE);
  let PRIORITY_TYPE__LOOKUP_MAP = PRIORITY_TYPE__LOOKUP_OPTIONS.reduce((map, obj) => {
    map[obj.value] = obj;
    return map;
  }, {});
  
  
  let MINUTES_FORMATTED_CACHE = {
    
    /** @type {Object.<number, string>} */
    cache: {},
    
    /** @param {number} minutes */
    get(minutes) {
      if (this.cache[minutes]) {
        return this.cache[minutes];
      }
      let formatted = DateTimeHelper.ConvertMinutesToHoursMinutes(minutes);
      this.cache[minutes] = formatted;
      return formatted;
    }
    
  };

  
  let items = rows.map((obj, index) => {
    
    ///////////////////////////////////////////////////////////////////////////////////
    // Flatten the object structure => outer event object with its inner task object.
    // obj.id must be used for main grid operation. 
    // Inner obj.task.id is used for comments.
    //
    let {
      plannedStartTime,
      plannedEndTime,
      plannedDuration,
      plannedResource,
      plannedTool,
      actualStartTime,
      actualEndTime,
      actualDuration,
      actualResource,
      actualTool,
      id,
      locked,
      split,
      valid,
      operationStatus,
      validationMessages,
      startTime,
      endTime,
      splitAssignments,
      toolCapacity,
      tokenValue,
      resource,
      productionLine,
      plannedSetupTimeMinutes,
      plannedSetupTimePercent,
    } = obj;

    let data = Object.assign({}, obj.task, {
      plannedStartTime,
      plannedEndTime,
      plannedDuration,
      plannedResource,
      plannedTool,
      actualStartTime,
      actualEndTime,
      actualDuration,
      actualResource,
      actualTool,
      id,
      locked,
      split,
      valid,
      operationStatus,
      validationMessages,
      startTime,
      endTime,
      splitAssignments,
      toolCapacity,
      tokenValue,
      resource,
      productionLine,
      plannedSetupTimeMinutes,
      plannedSetupTimePercent,
    });
    

    // Note: this is a work around - Server does not return an objectType for the Task
    // data.objectType = getEnumValue(enumTypes.OBJECT_TYPE)('TASK');
    data.objectType = E.OBECT_TYPE__TASK;
    
    // let plannedResourceData = data.plannedResource ? data.plannedResource : null;
    // let actualResourceData = data.actualResource ? data.actualResource : null;

    data.isRowMaster = true;

    data.taskId = obj.task?.id || null; // For comments, we use the task id.

    //------------FOR DEBUG ONLY !----------------
    // data.plannedTool = {businessId: 'aaaa-bbbb-ccccc-ddddddd'};
    // data.quantity = 2;
    // data.toolCapacity = 30;
    //----------------------------

    data.extendedDetails = {
      rows: [

        // 1st row -------
        {
          title: L.PLANNED_START_TIME,
          value:
            data.plannedStartTime && data.plannedStartTime.epochDateTime
              ? DateTimeHelper.DateTimeFormat(data.plannedStartTime.epochDateTime * 1000)
              : ' ',
        },
        {
          title: L.PLANNED_END_TIME,
          value:
            data.plannedEndTime && data.plannedEndTime.epochDateTime ? DateTimeHelper.DateTimeFormat(data.plannedEndTime.epochDateTime * 1000) : ' ',
        },
        {
          title: L.PLANNED_TOOL_ID,
          value: data.plannedTool ? data.plannedTool.businessId + ' ' + getQuantityInfo(data.quantity, data.toolCapacity) : '',
        },
        {
          title: L.START_TIME,
          value:
            data.actualStartTime && data.actualStartTime.epochDateTime
              ? DateTimeHelper.DateTimeFormat(data.actualStartTime.epochDateTime * 1000)
              : ' ',
        },

        // 2nd row -------
        {
          title: L.END_TIME,
          value:
            data.actualEndTime && data.actualEndTime.epochDateTime ? DateTimeHelper.DateTimeFormat(data.actualEndTime.epochDateTime * 1000) : ' ',
        },
        { title: L.TOOL_ID, value: data.actualTool ? data.actualTool.businessId : '' },
        {
          title: L.PLANNED_DURATION,
          // value: data.plannedDuration ? DateTimeHelper.ConvertMinutesToHoursMinutes(data.plannedDuration) : ' ',
          value: data.plannedDuration ? MINUTES_FORMATTED_CACHE.get(data.plannedDuration) : ' ',
        },
        { title: L.PROJECT, value: data.project ? data.project.name : ' ' },

        // 3rd row -------
        {
          title: L.TOOL_REQUIREMENT,
          value: data.toolRequirement ?? '',
        },
        {
          title: L.DURATION,
          // value: data.actualDuration ? DateTimeHelper.ConvertMinutesToHoursMinutes(data.actualDuration) : '',
          value: data.actualDuration ? MINUTES_FORMATTED_CACHE.get(data.actualDuration) : '',
        },
        {
          title: L.MATERIAL,
          value: data.material ? MaterialHelper.getMaterialFullLabel(data.material.name, data.material.businessId) : ' ',
        },
        { title: L.SPLIT_ENABLED, value: data.splittable ? 'True' : 'False' },

        // 4th row -------
        { title: L.RECIPE, value: data.groupName ? data.groupName : ' ' },
        { title: L.KITTYPE_DESCRIPTION, value: data.kitTypeDescription || ' ' },
        { title: L.TOKENS_DETAILS, value: getFormattedTokenDetails(data.tokenValue) },
        { title: L.SETUP_TIME, value: getFormattedSetupTime(data?.plannedSetupTimeMinutes, data?.plannedSetupTimePercent) },

        // 5th row -------
        // ...(data.operationStatus === getEnumValue(enumTypes.TASK_STATUS)('COMPLETED')
        ...(
          data.operationStatus === E.TASK_STATUS__COMPLETED
          ? [{ title: L.COMPLETED_QUANTITY, value: getCompletedQuantityInfo(data.completedQuantity, data.quantity) }]
          : []
        ),
      ],
      splitAssignments: data.splitAssignments,
    };

    // data.m_priority = getLookupOptions(enumTypes.PRIORITY_TYPE).find((x) => x.value === data.priority);
    // data.m_priority = PRIORITY_TYPE__LOOKUP_OPTIONS.find((x) => x.value === data.priority);
    data.m_priority = PRIORITY_TYPE__LOOKUP_MAP[data.priority];
    
    
    return data;
  });
  

  // console.log('items ===> ', items);
  // window.TTT = items; // For debugging purposes, remove in production
  
  // let t2 = performance.now();
  // console.log(`===> KONVERT END => ${t2 - t1} ms.`);

  
  return items;
}


export let actionTypes = gridActions.actionTypes;
export let jsxActions = gridActions.jsxActions;
export let api = gridActions.apiActions;





export let staticApi = {};


staticApi.getGridDataJS = function () {
  let { dispatch, getState } = StoreRegistry.getDefaultStore();
  
  let gridData = staticApi.getGridData();
  
  return gridData ? gridData.toJS() : null;
}

staticApi.getGridData = function () {
  let { dispatch, getState } = StoreRegistry.getDefaultStore();
  
  let state = getState();
  let gridData = state.grid.get(gridsNames.PREDEFINED_LIST_SCHEDULER_OPERATIONS) || null;
  
  return gridData;
}


/**
 * @param {any[]} newRows
 */
staticApi.updateGridRowData = function (newRows) {
  let { dispatch, getState } = StoreRegistry.getDefaultStore();
  
  gridActions.apiActions.updateGridRowData(newRows, gridsNames.PREDEFINED_LIST_SCHEDULER_OPERATIONS)(dispatch, getState);
  
}

staticApi.signalDataChanged = async function () {
  let { dispatch, getState } = StoreRegistry.getDefaultStore();
  
  gridActions.apiActions.signalDataChanged()(dispatch, getState);
  
}

/**
 * @param {number} taskId
 */
staticApi.onCommentAdded = async function (taskId) {
  let { dispatch, getState } = StoreRegistry.getDefaultStore();
  
  // console.log('onCommentAdded ==> taskId ===> ', taskId);
  
  // gridActions.apiActions.signalDataChanged()(dispatch, getState);
  
  let gridData = staticApi.getGridDataJS();
  if (!gridData) {
    return;
  }
  
  let rows = gridData.rows;
  // console.log('rows ===> ', rows);
  
  let foundRow = rows.find((/** @type {{ taskId: number; commentsCount: number }} */ row) => row.taskId === taskId);
  if (!foundRow) {
    return;
  }
  
  let currentCommentsCount = foundRow.commentsCount || 0;
  foundRow.commentsCount = currentCommentsCount + 1;
  
  staticApi.updateGridRowData(rows);
  
  
  // Signal grid renderer to run special effects.
  staticApi.signalDataChanged();
  
}





// // console.log('api ===> ', api);
// window.ZZAPI = api; // For debugging purposes, remove in production

// window.SSAPI = staticApi; // For debugging purposes, remove in production










